import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';

const TermsAndConditions: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <Link 
                  to="/" 
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Back to Home
                </Link>
                <div className="h-6 w-px bg-gray-300"></div>
                <Logo />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
            <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
          </div>

          <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using CodeSplash's website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p>
                CodeSplash provides custom software development services including but not limited to:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Web application development</li>
                <li>Mobile application development</li>
                <li>Graphic design and branding</li>
                <li>UI/UX design services</li>
                <li>Business software solutions</li>
                <li>E-commerce solutions</li>
                <li>Restaurant and service industry applications</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. User Accounts and Registration</h2>
              <p>
                To access certain features of our service, you must create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your account information</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept responsibility for all activities under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Service Requests and Projects</h2>
              <p>
                When submitting service requests or project proposals:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Provide clear and detailed project requirements</li>
                <li>Upload only files you have the right to use</li>
                <li>Ensure all content complies with applicable laws</li>
                <li>Understand that project timelines are estimates</li>
                <li>Maintain open communication throughout the project</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Payment Terms</h2>
              <p>
                Our payment terms are as follows:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Subscription plans are billed monthly or annually</li>
                <li>Payments are processed through secure payment gateways</li>
                <li>Late payments may result in service suspension</li>
                <li>Refunds are handled on a case-by-case basis</li>
                <li>Price changes will be communicated 30 days in advance</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Intellectual Property Rights</h2>
              <p>
                <strong>Your Content:</strong> You retain ownership of content you submit, including brand assets, project requirements, and feedback.
              </p>
              <p className="mt-3">
                <strong>Our Deliverables:</strong> Upon full payment, you receive a license to use the final deliverables for their intended purpose. We retain the right to showcase work in our portfolio.
              </p>
              <p className="mt-3">
                <strong>Third-Party Content:</strong> We may use third-party tools, libraries, and services. Their respective licenses and terms apply.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Project Delivery and Revisions</h2>
              <p>
                <strong>Delivery Timeline:</strong> We strive to deliver projects within estimated timeframes, but these are estimates and may vary based on project complexity and client feedback cycles.
              </p>
              <p className="mt-3">
                <strong>Revisions:</strong> Each project includes a specified number of revision rounds. Additional revisions may incur additional charges.
              </p>
              <p className="mt-3">
                <strong>Acceptance:</strong> Projects are considered complete when deliverables are accepted by the client or after the final revision round.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Confidentiality and Non-Disclosure</h2>
              <p>
                We understand the sensitive nature of business information and agree to:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Keep all client information confidential</li>
                <li>Use information only for project purposes</li>
                <li>Implement appropriate security measures</li>
                <li>Return or destroy confidential information upon request</li>
                <li>Maintain confidentiality obligations after project completion</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Limitation of Liability</h2>
              <p>
                CodeSplash's liability is limited to the amount paid for services in the 12 months preceding the claim. We are not liable for:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Indirect, incidental, or consequential damages</li>
                <li>Loss of profits, data, or business opportunities</li>
                <li>Third-party actions or content</li>
                <li>Force majeure events beyond our control</li>
                <li>Client misuse of delivered software or designs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Service Availability and Maintenance</h2>
              <p>
                We strive to maintain high service availability but cannot guarantee:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>100% uptime for our website and services</li>
                <li>Uninterrupted access to project management tools</li>
                <li>Immediate response to all support requests</li>
                <li>Compatibility with all devices and browsers</li>
              </ul>
              <p className="mt-3">
                Scheduled maintenance will be communicated in advance when possible.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Termination and Cancellation</h2>
              <p>
                <strong>By Client:</strong> You may cancel your subscription or project at any time with written notice. Refunds will be processed according to our refund policy.
              </p>
              <p className="mt-3">
                <strong>By CodeSplash:</strong> We may terminate services for violations of these terms, non-payment, or other material breaches. We will provide reasonable notice when possible.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Dispute Resolution</h2>
              <p>
                In the event of a dispute:
              </p>
              <ol className="list-decimal pl-6 mt-3 space-y-2">
                <li>Parties agree to attempt informal resolution first</li>
                <li>If unresolved, parties may seek mediation</li>
                <li>Legal action may be pursued in courts of competent jurisdiction</li>
                <li>Georgia law governs these terms and any disputes</li>
              </ol>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Force Majeure</h2>
              <p>
                Neither party shall be liable for delays or failures due to circumstances beyond reasonable control, including but not limited to:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2">
                <li>Natural disasters and weather events</li>
                <li>Government actions and regulations</li>
                <li>Internet and infrastructure failures</li>
                <li>Third-party service disruptions</li>
                <li>Pandemics and health emergencies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p>
                We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Continued use of our services constitutes acceptance of modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">15. Governing Law and Jurisdiction</h2>
              <p>
                These terms are governed by the laws of the State of Georgia, United States. Any legal proceedings shall be brought in the courts of Fulton County, Georgia.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">16. Severability</h2>
              <p>
                If any provision of these terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">17. Entire Agreement</h2>
              <p>
                These terms constitute the entire agreement between you and CodeSplash regarding our services and supersede all prior agreements and understandings.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">18. Contact Information</h2>
              <p>
                For questions about these terms, please contact us:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mt-3">
                <p className="font-medium">CodeSplash</p>
                <p>Email: legal@codesplash.com</p>
                <p>Phone: [Your Phone Number]</p>
                <p>Address: [Your Business Address]</p>
              </div>
            </section>
          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500 mb-4">
              By using our services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
            <div className="flex justify-center space-x-4">
              <Link 
                to="/privacy-policy" 
                className="text-blue-600 hover:text-blue-800 underline text-sm"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/contact" 
                className="text-blue-600 hover:text-blue-800 underline text-sm"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsAndConditions;
